"use client"

import { Children, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import type { ComponentProps, Ref } from "react"

import { Reel } from "@/components/ui/reel"
import { railKey, readBrowseValue, registerBrowseCapture, subscribeBrowseSnapshots, writeBrowseValue } from "@/platform/app-mode/browse-history"
import { useBrowseEntry, useBrowseReadiness } from "@/platform/app-mode/use-browse-value"

type ReelProps = ComponentProps<typeof Reel>

interface WindowedReelProps extends Omit<ReelProps, "children"> {
  windowSize: number
  children?: ReelProps["children"]
}

const LOOKAHEAD = 1.5

export const WindowedReel = ({ windowSize, ref, children, ...rest }: WindowedReelProps) => {
  const entryId = useBrowseEntry()
  const owner = useRef(entryId)
  const scroller = useRef<HTMLElement | null>(null)
  const items = Children.toArray(children)
  const [mounted, setMounted] = useState(() => Math.min(windowSize, items.length))
  const mountedRef = useRef(mounted)
  mountedRef.current = mounted
  useLayoutEffect(() => {
    const element = scroller.current
    if (!element) return
    const key = `rail:${railKey(element)}`
    const fallback = owner.current === entryId ? mountedRef.current : windowSize
    owner.current = entryId
    const restore = () => setMounted(Math.min(items.length, readBrowseValue(key, fallback, entryId)))
    restore()
    const off = subscribeBrowseSnapshots(restore)
    const capture = registerBrowseCapture(() => writeBrowseValue(key, mountedRef.current, entryId), entryId)
    return () => {
      off()
      capture()
    }
  }, [entryId, items.length, windowSize])
  const key = scroller.current ? `rail:${railKey(scroller.current)}` : ""
  useBrowseReadiness(mounted >= Math.min(items.length, readBrowseValue(key, windowSize, entryId)), entryId)
  const hasMore = mounted < items.length

  const attach = useCallback(
    (element: HTMLElement | null) => {
      scroller.current = element
      if (typeof ref === "function") ref(element)
      else if (ref) (ref as { current: HTMLElement | null }).current = element
    },
    [ref]
  )

  const grow = useCallback(() => {
    const element = scroller.current
    if (!element || element.clientWidth === 0) return
    if (element.scrollLeft + element.clientWidth * LOOKAHEAD < element.scrollWidth) return
    setMounted((current) => Math.min(items.length, current + windowSize))
  }, [items.length, windowSize])

  useEffect(() => {
    if (hasMore) grow()
  })

  useEffect(() => {
    const element = scroller.current
    if (!hasMore || !element) return
    let frame = 0
    const onScroll = () => {
      if (frame !== 0) return
      frame = requestAnimationFrame(() => {
        frame = 0
        grow()
      })
    }
    element.addEventListener("scroll", onScroll, { passive: true })
    const observer = new ResizeObserver(onScroll)
    observer.observe(element)
    return () => {
      element.removeEventListener("scroll", onScroll)
      observer.disconnect()
      if (frame) cancelAnimationFrame(frame)
    }
  }, [hasMore, grow])

  return (
    <Reel ref={attach as Ref<HTMLElement>} {...rest}>
      {items.slice(0, mounted)}
    </Reel>
  )
}
